import { Badge, CardBody, CardHeader, Skeleton } from '@/components/ui';

import { Button, Card } from '@/components/ui';
import { Icon } from '@iconify/react/dist/iconify.js';
import type { ReactNode } from 'react';

interface DashboardCardProps {
  children: ReactNode;
  className?: string;
  loading?: boolean;
  empty?: boolean;
  emptyContent?: ReactNode;
}

interface HeaderProps {
  title: string;
  subtitle?: string;
  count?: number;
  onViewAll?: () => void;
}

interface BodyProps {
  children: ReactNode;
  scrollable?: boolean;
  maxHeight?: string | number;
  empty?: boolean;
  emptyContent?: ReactNode;
}

function DashboardCard({
  children,
  className = '',
  loading = false,
}: DashboardCardProps) {
  return (
    <div className={`flex flex-col py-2 ${className}`}>
      <Card className='max-w-md' shadow='none'>
        {loading ? (
          <>
            <CardHeader className='flex flex-row items-center justify-between'>
              <div className='flex flex-col items-start gap-2'>
                <Skeleton className='h-6 w-24' />
                <Skeleton className='h-2 w-12' />
              </div>
              <Skeleton className='h-6 w-20' />
            </CardHeader>
            <CardBody>
              <div className='divide-y divide-gray-200 rounded-xl border-1 p-3'>
                {[...Array(4)].map((_, index) => (
                  <div key={index} className='py-3'>
                    <div className='flex items-start gap-4 py-1'>
                      <Skeleton className='h-5 w-5 rounded-full' />
                      <div className='flex-1'>
                        <Skeleton className='mb-2 h-4 w-3/4' />
                        <Skeleton className='h-3 w-1/3' />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </>
        ) : (
          children
        )}
      </Card>
    </div>
  );
}

function Header({ title, subtitle, count, onViewAll }: HeaderProps) {
  const showCount = Boolean(count);

  return (
    <CardHeader>
      <div className='flex w-full items-center justify-between'>
        <div className='flex flex-col items-start'>
          <div className='flex flex-row items-center'>
            <p className='pr-2 pt-0.5 text-sm font-medium leading-5'>{title}</p>
            {showCount && (
              <Badge
                color='danger'
                size='md'
                className='text-xs'
                content={count}
              >
                <div className='ml-2' />
              </Badge>
            )}
          </div>
          {subtitle && (
            <span className='text-sm italic leading-5 text-[#92929D]'>
              {subtitle}
            </span>
          )}
        </div>
        {onViewAll && (
          <Button
            size='sm'
            variant='flat'
            endContent={<Icon icon='mdi:arrow-right' width={20} height={20} />}
            onPress={onViewAll}
          >
            View All
          </Button>
        )}
      </div>
    </CardHeader>
  );
}

function Body({ children, empty, emptyContent }: BodyProps) {
  return (
    <CardBody>
      <div className='flex flex-col gap-2 rounded-xl border-1'>
        <div className='scrollbar-thin max-h-[300px] gap-2 overflow-y-auto'>
          <div className='flex flex-col gap-2 p-3'>
            {empty ? (emptyContent ?? null) : children}
          </div>
        </div>
      </div>
    </CardBody>
  );
}

DashboardCard.Header = Header;
DashboardCard.Body = Body;

export { DashboardCard };
